export type DocGroupId = 'overview' | 'contact' | 'shared' | 'server' | 'pipeline' | 'other';

/**
 * How a doc should be rendered. `md` is GitHub-flavoured markdown; `svg` is a
 * standalone vector image rendered inline; `html` is a self-contained rich
 * page (a "design file" — HTML + CSS + inline SVG) rendered in an isolated
 * sandboxed iframe so its styling can't collide with the app shell.
 */
export type DocKind = 'md' | 'svg' | 'html';

// Platforms that render as their own top-level nav section (Architecture,
// Salesforce) rather than inside the general /docs browser. getDocsByGroup
// excludes them so they don't double-appear; PlatformDocs renders them by
// filtering getAllDocs() on platformId.
export const SECTION_PLATFORMS = ['architecture', 'salesforce', 'azure', 'semarchy', 'netsuite', 'creatio'];

export interface DocMeta {
  /** `<platformId>/<path>` (no extension). The platform id is the first segment. */
  slug: string;
  platformId: string;
  title: string;
  description?: string;
  group: DocGroupId;
  groupLabel: string;
  /** Markdown source, raw SVG markup, or a self-contained HTML document. */
  content: string;
  kind: DocKind;
}

// Markdown, standalone SVG images, and self-contained HTML design files are all
// picked up. `?raw` gives us each file's text verbatim so the renderer can
// decide how to present it based on `kind`.
const modules = import.meta.glob('./docs/**/*.{md,svg,html}', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

function kindFromPath(path: string): DocKind {
  if (path.endsWith('.svg')) return 'svg';
  if (path.endsWith('.html')) return 'html';
  return 'md';
}

function stripFrontmatter(raw: string): { meta: Record<string, string>; content: string } {
  const m = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!m) return { meta: {}, content: raw };
  const meta: Record<string, string> = {};
  for (const line of m[1].split(/\r?\n/)) {
    const kv = line.match(/^([A-Za-z][A-Za-z0-9_-]*)\s*:\s*(.*)$/);
    if (kv) meta[kv[1]] = kv[2].trim().replace(/^["']|["']$/g, '');
  }
  return { meta, content: m[2] };
}

function classify(platformId: string, pathWithinPlatform: string): { group: DocGroupId; groupLabel: string } {
  // ADO pipeline docs live under pipelines/*.md; they're a single logical
  // section on the docs index and don't follow the per-platform/per-workflow
  // grouping the other docs use.
  if (platformId === 'pipelines') return { group: 'pipeline', groupLabel: 'ADO Pipelines' };
  if (pathWithinPlatform.startsWith('workflows/syn-contact-')) return { group: 'contact', groupLabel: 'Contact operations' };
  if (pathWithinPlatform.startsWith('workflows/syn-shared-')) return { group: 'shared', groupLabel: 'Shared workflows' };
  if (pathWithinPlatform.startsWith('workflows/syn-server-')) return { group: 'server', groupLabel: 'Server' };
  if (!pathWithinPlatform.includes('/') || pathWithinPlatform.startsWith('overview')) {
    return { group: 'overview', groupLabel: 'Overview' };
  }
  return { group: 'other', groupLabel: 'Other' };
}

function titleFromSlug(slug: string): string {
  const tail = slug.split('/').pop() ?? slug;
  return tail.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function deriveTitle(slug: string, content: string, kind: DocKind, frontmatterTitle?: string): string {
  if (frontmatterTitle) return frontmatterTitle;
  if (kind === 'html') {
    // Prefer <title>, then the first <h1>, else the filename.
    const t = content.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
    if (t && t[1].trim()) return t[1].trim();
    const h1 = content.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
    if (h1) return h1[1].replace(/<[^>]+>/g, '').trim();
    return titleFromSlug(slug);
  }
  if (kind === 'svg') {
    // SVGs may carry a <title>; otherwise fall back to the filename.
    const t = content.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
    if (t && t[1].trim()) return t[1].trim();
    return titleFromSlug(slug);
  }
  const h1 = content.match(/^#\s+(.+)$/m);
  if (h1) return h1[1].trim();
  return titleFromSlug(slug);
}

const GROUP_ORDER: DocGroupId[] = ['overview', 'contact', 'shared', 'server', 'pipeline', 'other'];

const docs: DocMeta[] = Object.entries(modules)
  .map(([path, raw]) => {
    const kind = kindFromPath(path);
    const slug = path.replace(/^\.\/docs\//, '').replace(/\.(md|svg|html)$/, '');
    const [platformId, ...rest] = slug.split('/');
    const pathWithinPlatform = rest.join('/');
    // Only markdown carries YAML frontmatter; SVG/HTML are used verbatim.
    const { meta, content } = kind === 'md' ? stripFrontmatter(raw) : { meta: {}, content: raw };
    const { group, groupLabel } = classify(platformId, pathWithinPlatform);
    return {
      slug,
      platformId,
      title: deriveTitle(slug, content, kind, meta.title),
      description: meta.description,
      group,
      groupLabel,
      content,
      kind,
    };
  })
  .sort((a, b) => {
    if (a.platformId !== b.platformId) return a.platformId.localeCompare(b.platformId);
    const ga = GROUP_ORDER.indexOf(a.group);
    const gb = GROUP_ORDER.indexOf(b.group);
    if (ga !== gb) return ga - gb;
    return a.title.localeCompare(b.title);
  });

export function getAllDocs(): DocMeta[] {
  return docs;
}

export function getDoc(slug: string): DocMeta | undefined {
  return docs.find((d) => d.slug === slug);
}

export function getPlatformIds(): string[] {
  return Array.from(new Set(docs.map((d) => d.platformId)));
}

const EMPTY_GROUPS = (): Record<DocGroupId, { label: string; docs: DocMeta[] }> => ({
  overview: { label: 'Overview', docs: [] },
  contact: { label: 'Contact operations', docs: [] },
  shared: { label: 'Shared workflows', docs: [] },
  server: { label: 'Server', docs: [] },
  pipeline: { label: 'ADO Pipelines', docs: [] },
  other: { label: 'Other', docs: [] },
});

export function getDocsByPlatformAndGroup(): Record<string, Record<DocGroupId, { label: string; docs: DocMeta[] }>> {
  const out: Record<string, Record<DocGroupId, { label: string; docs: DocMeta[] }>> = {};
  for (const d of docs) {
    if (!out[d.platformId]) out[d.platformId] = EMPTY_GROUPS();
    out[d.platformId][d.group].docs.push(d);
  }
  return out;
}

export function getDocsByGroup(): Record<DocGroupId, { label: string; docs: DocMeta[] }> {
  const out = EMPTY_GROUPS();
  // Exclude section platforms (Architecture, Salesforce) — they have their own
  // top-level nav section and must not also appear in the general /docs browser.
  for (const d of docs) {
    if (SECTION_PLATFORMS.includes(d.platformId)) continue;
    out[d.group].docs.push(d);
  }
  return out;
}

/** Docs for a single platform (used by the Architecture / Salesforce sections). */
export function getDocsForPlatform(platformId: string): DocMeta[] {
  return docs.filter((d) => d.platformId === platformId);
}
